import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { unwrapPaginated } from "@/config/api";
import { formatDateTime } from "@/i18n/format";
import { getApiErrorMessage } from "@/lib/apiError";
import { isCoordinatorQuery, stallRuleLabel } from "@/lib/coordinator";
import type { CoordinatorStatus, DealStallFlag } from "@/services/moderators/generated/model";
import {
  useModeratorsCoordinatorStalledRetrieve,
  useModeratorsCoordinatorStallScanCreate,
} from "@/services/moderators/generated/moderators-coordinator/moderators-coordinator";

/**
 * WP2.6 — stalled deals on the coordinator queue page: "Run stall scan"
 * (deterministic rules, no AI) and the active flags. Auto-drafted reminders
 * still go through the approval queue.
 */
const CoordinatorStallPanel = ({ status }: { status?: CoordinatorStatus }) => {
  const { t } = useTranslation(["deals", "common"]);
  const qc = useQueryClient();
  const [autoDraft, setAutoDraft] = useState(true);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { data, isLoading } = useModeratorsCoordinatorStalledRetrieve(
    { page_size: 100 },
    { query: { staleTime: 0, refetchOnMount: true } },
  );
  const flags = unwrapPaginated<DealStallFlag>(data);
  const scan = useModeratorsCoordinatorStallScanCreate({
    mutation: { onSuccess: () => qc.invalidateQueries({ predicate: (q) => isCoordinatorQuery(q.queryKey) }) },
  });
  const thresholds = status?.stallThresholds;

  const run = () =>
    scan.mutate(
      { data: { autoDraft } },
      {
        onSuccess: (res) => {
          setError(null);
          setResult(
            t("coordinator.stall.result", {
              scanned: res.scanned,
              flagged: res.flagged,
              still: res.stillFlagged,
              resolved: res.resolved,
              drafts: res.draftsCreated,
            }),
          );
        },
        onError: (err) => setError(getApiErrorMessage(err, t("coordinator.stall.errors.scanFailed"))),
      },
    );

  return (
    <section className="mod-page__card">
      <div className="mod-page__card-header">
        <h2 className="mod-page__card-title">{t("coordinator.stall.title", { count: flags.length })}</h2>
        <div className="coord-draft__actions">
          <label className="coord-draft__log">
            <input type="checkbox" checked={autoDraft} onChange={(e) => setAutoDraft(e.target.checked)} />
            {t("coordinator.stall.autoDraft")}
          </label>
          <button
            type="button"
            className="mod-page__btn mod-page__btn--primary"
            disabled={scan.isPending}
            onClick={run}
          >
            {scan.isPending ? t("coordinator.stall.scanning") : t("coordinator.stall.runScan")}
          </button>
        </div>
      </div>
      {thresholds ? (
        <p className="mod-page__card-meta">
          {t("coordinator.stall.thresholds", {
            consent: thresholds.pendingConsentDays,
            messages: thresholds.noMessageDays,
            stage: thresholds.noStageChangeDays,
          })}
        </p>
      ) : null}
      {result ? <p className="mod-page__card-meta">{result}</p> : null}
      {error ? <p className="mod-modal__error">{error}</p> : null}

      {isLoading ? <p className="mod-page__loading">{t("common:states.loading")}</p> : null}
      {!isLoading && flags.length === 0 ? <p className="mod-page__empty">{t("coordinator.stall.empty")}</p> : null}
      {flags.length > 0 ? (
        <table className="coord-panel__table">
          <thead>
            <tr>
              <th>{t("coordinator.stall.columns.deal")}</th>
              <th>{t("coordinator.stall.columns.parties")}</th>
              <th>{t("coordinator.stall.columns.reason")}</th>
              <th>{t("coordinator.stall.columns.moderator")}</th>
              <th>{t("coordinator.stall.columns.flagged")}</th>
            </tr>
          </thead>
          <tbody>
            {flags.map((f) => (
              <tr key={f.id}>
                <td>
                  <Link to={`/moderator/deals/${f.dealId}`}>
                    {t("coordinator.draft.deal", { id: f.dealId, title: f.dealTitle })}
                  </Link>
                </td>
                <td>
                  {f.buyerName} ↔ {f.supplierName}
                </td>
                <td>{stallRuleLabel(f.rule, f.days)}</td>
                <td>{f.assignedModeratorName ?? t("shared.unassigned")}</td>
                <td>{formatDateTime(f.flaggedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
};

export default CoordinatorStallPanel;
